import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import RichText from '@eneco/components/RichText/RichText';
import {
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberProductSwitchOrderByOrderId,
  GetEnecoBeXapiSiteApiV1AccountsByAccountNumberProductSwitchOrderByOrderIdData,
  getEnecoBeXapiSiteApiV1ProductSwitchOrderByOrderIdAccountNumber,
} from '@eneco/dc-be/client';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { Stretch, Stack, Heading, ButtonLink } from '@eneco/sparky';
import { useContent } from '@sitecore/common';
import { ProductSwitchRenderingExtended } from '@sitecore/types/manual/be/ProductSwitchExtended';

import ProductSwitchOverview from './ProductSwitchOverview';
import getSortedProducts from './utils/getSortedProducts';
import CenteredCardLayout from '../../../components/CenteredCardLayout';

const ProductSwitchMailLandingPage = ({ orderId }: { orderId: string }) => {
  const { data: session } = useSession();
  const { selectedAccount, isCurrentAccountReader } = useSelfServiceAccount();

  const {
    data: accountNumberWrapped,
    isLoading: isLoadingAccountNumber,
    error: errorAccountNumber,
  } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1ProductSwitchOrderByOrderIdAccountNumber,
    { path: { orderId } },
    [`/product-switch/order/${orderId}`],
    session,
  );

  const accountNumber = unwrapData(accountNumberWrapped)?.accountNumber;

  const path: GetEnecoBeXapiSiteApiV1AccountsByAccountNumberProductSwitchOrderByOrderIdData['path'] = {
    accountNumber: accountNumber ?? selectedAccount.crmAccountNumber,
    orderId,
  };

  const {
    data: dataWrapped,
    isLoading,
    error,
  } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberProductSwitchOrderByOrderId,
    {
      path,
    },
    [
      `/accounts/${accountNumber}/product-switch/order/${orderId}`,
      `/accounts/${selectedAccount.crmAccountNumber}/product-switch/order/${orderId}`,
    ],
    session,
    { shouldFetch: !!accountNumber && !!orderId && !isLoadingAccountNumber },
  );

  const data = unwrapData(dataWrapped);

  if (isLoading || isLoadingAccountNumber) {
    return null;
  }

  if (dataWrapped?.error?.errors || error || errorAccountNumber || accountNumberWrapped?.error?.errors) {
    return <ExpiredToken />;
  }

  if (!data) {
    return null;
  }

  const sortedProducts = getSortedProducts(data?.records, null, isCurrentAccountReader);

  return <ProductSwitchOverview sortedProducts={sortedProducts} hasOrderId />;
};

const ExpiredToken = () => {
  const { fields } = useContent<ProductSwitchRenderingExtended>();
  return (
    <CenteredCardLayout>
      <Heading as="h3" size="S">
        {fields.mailConfirmation.title.value}
      </Heading>
      <RichText html={fields.mailConfirmation.content.value}></RichText>
      <Stack gap="4">
        <Stretch>
          <ButtonLink
            href={fields.mailConfirmation.primaryButtonLink.value.href}
            target={fields.mailConfirmation.primaryButtonLink.value.target}>
            {fields.mailConfirmation.primaryButtonLink.value.text}
          </ButtonLink>
        </Stretch>
        <Stretch>
          <ButtonLink
            href={fields.mailConfirmation.secondaryButtonLink.value.href}
            target={fields.mailConfirmation.secondaryButtonLink.value.target}
            action="secondary">
            {fields.mailConfirmation.secondaryButtonLink.value.text}
          </ButtonLink>
        </Stretch>
      </Stack>
    </CenteredCardLayout>
  );
};

export default ProductSwitchMailLandingPage;
